import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { getConversionsFromFormat } from '../data/conversions';
import { ImageFormat } from '../services/imageService';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  
  const FORMAT_LABELS: Partial<Record<ImageFormat, string>> = {
    jpeg: 'JPG', png: 'PNG', webp: 'WebP', avif: 'AVIF',
    gif: 'GIF', bmp: 'BMP', ico: 'ICO', svg: 'SVG',
  };
  
  const SOURCE_FORMATS: ImageFormat[] = ['png', 'jpeg', 'webp', 'svg', 'gif', 'bmp'];
  
  // Ensure the link includes the language prefix
  const getLanguagePrefixedPath = (path: string) => {
    // If path is root, just return the language prefix
    if (path === '/') {
      return `/${lang || 'en'}`;
    }
    
    // Don't add language prefix to admin routes
    if (path.startsWith('/admin')) {
      return path;
    }
    
    // Otherwise, add the language prefix
    return `/${lang || 'en'}${path.startsWith('/') ? path : `/${path}`}`;
  };

  return (
    <footer className="bg-primary-950 text-gray-100" role="contentinfo">
      <div className="container mx-auto">
        {/* Conversion Links Section */}
        <div className="py-12 border-b border-primary-800">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Column 1: From PNG */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                From PNG
              </p>
              <ul className="space-y-2 pl-0">
                {getConversionsFromFormat('png').map(conv => (
                  <li key={conv.slug} className="mb-2">
                    <Link
                      to={`/${lang || 'en'}/convert/${conv.slug}`}
                      className="text-sm text-gray-600 hover:text-primary-300 transition-colors"
                    >
                      PNG → {FORMAT_LABELS[conv.to] ?? conv.to.toUpperCase()}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2: From JPG */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                From JPG
              </p>
              <ul className="space-y-2 pl-0">
                {getConversionsFromFormat('jpeg').map(conv => (
                  <li key={conv.slug} className="mb-2">
                    <Link
                      to={`/${lang || 'en'}/convert/${conv.slug}`}
                      className="text-sm text-gray-600 hover:text-primary-300 transition-colors"
                    >
                      JPG → {FORMAT_LABELS[conv.to] ?? conv.to.toUpperCase()}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: From WebP + From GIF */}
            <div className="space-y-6">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  From WebP
                </p>
                <ul className="space-y-2 pl-0">
                  {getConversionsFromFormat('webp').map(conv => (
                    <li key={conv.slug} className="mb-2">
                      <Link
                        to={`/${lang || 'en'}/convert/${conv.slug}`}
                        className="text-sm text-gray-600 hover:text-primary-300 transition-colors"
                      >
                        WebP → {FORMAT_LABELS[conv.to] ?? conv.to.toUpperCase()}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  From GIF
                </p>
                <ul className="space-y-2 pl-0">
                  {getConversionsFromFormat('gif').map(conv => (
                    <li key={conv.slug} className="mb-2">
                      <Link
                        to={`/${lang || 'en'}/convert/${conv.slug}`}
                        className="text-sm text-gray-600 hover:text-primary-300 transition-colors"
                      >
                        GIF → {FORMAT_LABELS[conv.to] ?? conv.to.toUpperCase()}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Column 4: From SVG + From BMP */}
            <div className="space-y-6">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  From SVG
                </p>
                <ul className="space-y-2 pl-0">
                  {getConversionsFromFormat('svg').map(conv => (
                    <li key={conv.slug} className="mb-2">
                      <Link
                        to={`/${lang || 'en'}/convert/${conv.slug}`}
                        className="text-sm text-gray-600 hover:text-primary-300 transition-colors"
                      >
                        SVG → {FORMAT_LABELS[conv.to] ?? conv.to.toUpperCase()}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  From BMP
                </p>
                <ul className="space-y-2 pl-0">
                  {getConversionsFromFormat('bmp').map(conv => (
                    <li key={conv.slug} className="mb-2">
                      <Link
                        to={`/${lang || 'en'}/convert/${conv.slug}`}
                        className="text-sm text-gray-600 hover:text-primary-300 transition-colors"
                      >
                        BMP → {FORMAT_LABELS[conv.to] ?? conv.to.toUpperCase()}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-center py-12 gap-8 md:gap-6">
          <div className="mb-4 md:mb-0">
            <Link to={`/${lang || i18n.language.split('-')[0]}`}>
              <img src="/logo-inverse.svg" alt="ShiftPic Logo" width={144} height={40} className="h-8" />
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
            <Link to={getLanguagePrefixedPath('/')} className="text-gray-600 hover:text-primary-300">
              {t('nav.converter')}
            </Link>
            <Link to={getLanguagePrefixedPath('/articles')} className="text-gray-600 hover:text-primary-300">
              {t('nav.articles')}
            </Link>
            <Link to={getLanguagePrefixedPath('/plugins')} className="text-gray-600 hover:text-primary-300">
              {t('nav.plugins')}
            </Link>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 md:gap-0 py-6 border-t border-primary-800 text-center text-gray-600 text-sm">
          <LanguageSwitcher />
          <div className="flex flex-col md:flex-row gap-4">
            <span>
              © {new Date().getFullYear()} All rights reserved, ShiftPic
            </span>
            <div className="flex gap-4">
              <Link to={getLanguagePrefixedPath('/legal')} className="text-gray-600 hover:text-primary-300 underline hover:no-underline">
                  {t('nav.legal')}
              </Link>
              <Link to={getLanguagePrefixedPath('/cookie-policy')} className="text-gray-600 hover:text-primary-300 underline hover:no-underline">
                  {t('nav.cookiePolicy')}
              </Link>
              <Link to={getLanguagePrefixedPath('/faq')} className="text-gray-600 hover:text-primary-300 underline hover:no-underline">
                  {t('nav.faq')}
              </Link>
            </div>
            <Link to={getLanguagePrefixedPath('/release-notes')} className="text-gray-600 hover:text-primary-300 underline hover:no-underline">
              {t('nav.releaseNotes')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;